import React from "react";
import { Flag, Timer, Droplets, Zap, Printer, ClipboardCopy, FileDown } from "lucide-react";
import { toast } from "sonner";
import AiRaceBrief from "@/components/race/AiRaceBrief";
import { downloadRacePlanPdf, openRacePlanPrintView } from "@/components/race/racePdf";
import TimeField from "@/components/TimeField";
import {
  SectionCard, CountdownBlock, FuellingTimetable, ElectrolytesCard, WhatToCarry,
  SweatHydrationCard, GutTrainingCard, MorningMealBlock, TopUpBlock, InRaceBlock,
  PostRaceBlock, TimelineBlock, ChecklistBlock, racePlanText, racePlanSections,
  DAY_NAMES, fmtMin, capPreCarbs, capPostCarbs, feedSchedule,
} from "@/components/race/RaceBlocks";

const LIGHT = { green: "#34C759", amber: "#FF9500", red: "#FF3B30" };

// THE race plan. Rendered identically for full subscribers (Race Hub) and
// one-off Race Pack buyers — one component, so the two can never drift apart.
export default function RacePlanBody({
  name, raceIso, startTime, onStartTime, raceMins, onRaceMins, planId,
  n, weightKg, daily = [], fuelling = [], raceIdx, strategy, sport, distance,
}) {
  const raceFuel = fuelling[raceIdx] || {};
  const raceSession = (raceFuel.sessions || [])[0];
  // The glance figures read off the same schedule as the timetable.
  const sched = React.useMemo(() => feedSchedule(n, startTime), [n, startTime]);

  const [brief, setBrief] = React.useState(null);

  const copyPlan = async () => {
    try {
      await navigator.clipboard.writeText(racePlanText({
        name, raceIso, startTime, n, weightKg, dailyTargets: daily, brief }));
      toast.success("Plan copied — paste it into TrainingPeaks or an email.");
    } catch {
      toast.error("Couldn't copy automatically — try the PDF instead.");
    }
  };

  const exportDoc = () => racePlanSections({
    name, raceIso, startTime, n, weightKg, dailyTargets: daily,
    fuelling, raceIdx, raceSession, strategy, brief, sport });

  const savePdf = async () => {
    toast.info("Building your PDF…");
    try {
      await downloadRacePlanPdf(exportDoc(),
        `${(name || "race-plan").replace(/[^\w-]+/g, "-").toLowerCase()}-fuelling-plan.pdf`);
      toast.success("PDF saved.");
    } catch {
      toast.error("Couldn't build the PDF — try Print instead.");
    }
  };

  const printPlan = () => {
    if (!openRacePlanPrintView(exportDoc())) {
      toast.error("Allow pop-ups for this site to print your plan.");
    }
  };

  return (
    <div className="space-y-4">
      {/* Controls + exports */}
      <div className="flex flex-wrap items-center gap-3 border border-white/10 bg-[#141414] px-4 py-3 no-print"
        data-testid="race-plan-controls">
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold flex items-center gap-2">
          <Timer className="h-3.5 w-3.5 text-[#E2E30A]" /> Race start
        </label>
        <TimeField value={startTime} onChange={(v) => onStartTime?.(v)} testid="race-start-time-input" />
        <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-bold flex items-center gap-2">
          Expected finish (mins)
        </label>
        <input type="number" min="20" max="900" value={raceMins}
          onChange={(e) => onRaceMins?.(e.target.value)} placeholder="180"
          data-testid="race-expected-mins-input"
          className="bg-black border border-white/10 text-white text-sm px-2 py-1 focus:outline-none focus:border-[#E2E30A] font-mono w-[5.5rem]" />
        <span className="text-[11px] text-zinc-500">Everything auto-times from these.</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <button type="button" data-testid="race-plan-pdf" onClick={savePdf}
            className="inline-flex items-center gap-1.5 bg-[#E2E30A] hover:bg-white text-black px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors">
            <FileDown className="h-3.5 w-3.5" /> Download PDF
          </button>
          <button type="button" data-testid="race-plan-copy" onClick={copyPlan}
            className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[#E2E30A]/60 px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-200 hover:text-white transition-colors">
            <ClipboardCopy className="h-3.5 w-3.5" /> Copy for TrainingPeaks
          </button>
          <button type="button" data-testid="race-plan-print" onClick={printPlan}
            className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[#E2E30A]/60 px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-200 hover:text-white transition-colors">
            <Printer className="h-3.5 w-3.5" /> Print
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <CountdownBlock raceIso={raceIso} startTime={startTime} name={name}
          distance={distance} durationMin={n.durMin} />

        {/* Race day at a glance */}
        <section className="border-2 border-[#E2E30A] bg-[#E2E30A]/[0.05] p-5 sm:p-6" data-testid="race-day-card">
          <p className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#E2E30A] font-bold">
            <Flag className="h-3.5 w-3.5" /> Race day at a glance
          </p>
          <div className="mt-5 grid sm:grid-cols-3 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Before</p>
              <p className="font-display text-3xl font-black tabular-nums mt-1">
                {capPreCarbs(raceSession?.preG ?? weightKg * 2, weightKg, n.durMin)}g
              </p>
              <p className="text-xs text-zinc-400">
                carbs · {Math.min(raceSession?.preFluidMl ?? 500, 750)}ml fluid
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">During · per hour</p>
              <p className="font-display text-3xl font-black tabular-nums mt-1">{n.cph}g</p>
              <p className="text-xs text-zinc-400">
                {sched.cumC}g total · {n.fluidPerHr}ml/hr · {n.naPerHr}mg sodium/hr
              </p>            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">After</p>
              <p className="font-display text-3xl font-black tabular-nums mt-1">
                {capPostCarbs(raceSession?.postG ?? weightKg, weightKg)}g
              </p>
              <p className="text-xs text-zinc-400">
                carbs · {Math.min(raceSession?.postFluidMl ?? 750, 1000)}ml fluid
              </p>
            </div>
          </div>
          {raceSession?.note && <p className="text-sm text-zinc-200 mt-5">{raceSession.note}</p>}
          <p className="text-xs text-zinc-400 mt-2">
            Hydration: {n.fluidPerHr}ml/hr with ~{n.naPerHr}mg sodium/hr
            {n.heat ? " (hot conditions)" : ""} — capped to what's sensible for a {fmtMin(n.durMin)} race.
          </p>
          {strategy && (
            <p className="text-xs text-zinc-400 mt-3 border-t border-[#E2E30A]/25 pt-3">{strategy}</p>
          )}
        </section>

        <div className="no-print"><AiRaceBrief onBrief={setBrief} planId={planId} durMin={n.durMin}
          numbers={{
            plannedPerHr: n.cph, carbTotal: sched.cumC, feedEveryMinutes: n.feedInterval,
            gelsTotal: sched.counts?.gel || 0, fluidPerHr: n.fluidPerHr, sodiumPerHr: n.naPerHr,
          }} /></div>

        {!n.durMin ? (
          <div className="border border-[#E2E30A]/40 bg-[#E2E30A]/[0.05] p-4" data-testid="race-needs-duration">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#E2E30A] font-bold">One number needed</p>
            <p className="text-sm text-zinc-200 mt-1.5">
              Enter your <b>expected finish time in minutes</b> above and we'll build your 20-minute
              fuelling timetable, sodium targets and what to carry.
            </p>
          </div>
        ) : (
          <>
            <FuellingTimetable n={n} startTime={startTime} />
            <div className="grid lg:grid-cols-2 gap-4">
              <InRaceBlock n={n} sport={sport} />
              <ElectrolytesCard n={n} startTime={startTime} />
              <WhatToCarry n={n} startTime={startTime} />
              <SweatHydrationCard n={n} />
              <MorningMealBlock startTime={startTime} weightKg={weightKg} durMin={n.durMin} />
              <TopUpBlock startTime={startTime} weightKg={weightKg} />
              <PostRaceBlock weightKg={weightKg} />
              <GutTrainingCard n={n} />
            </div>
          </>
        )}

        <TimelineBlock raceIso={raceIso} weightKg={weightKg} />

        {!!daily.length && (
          <SectionCard icon={Zap} color="#E2E30A" title="Race Week Daily Targets"
            sub="Every day into the race" testid="race-week-targets">
            <div className="space-y-2">
              {daily.map((d, i) => {
                const f = fuelling[i];
                const isRace = i === raceIdx;
                return (
                  <div key={i} data-testid={`race-week-day-${i}`}
                    className={`p-3 border ${isRace ? "border-[#E2E30A]/60 bg-[#E2E30A]/[0.04]" : "border-white/10 bg-black/30"}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-display text-base uppercase tracking-tight">
                        {DAY_NAMES[i] || `Day ${i + 1}`}
                        {isRace && <span className="ml-2 text-[10px] uppercase tracking-widest text-[#E2E30A]">Race</span>}
                        {d.carbLoadBumpGkg > 0 && (
                          <span className="ml-2 text-[10px] uppercase tracking-widest text-[#FF9500]">Carb load +{d.carbLoadBumpGkg}g/kg</span>
                        )}
                      </p>
                      <p className="text-[13px] tabular-nums text-zinc-200">
                        <span className="text-white font-bold">{d.energy}</span> kcal · {d.carb}g C · {d.protein}g P · {d.fat}g F
                      </p>
                    </div>
                    <p className="flex flex-wrap items-center gap-4 text-[11px] text-zinc-400 mt-1.5">
                      <span className="inline-flex items-center gap-1.5"><Droplets className="h-3 w-3" /> {d.hydrationMl}ml</span>
                      {!!f?.light && (
                        <span className="inline-flex items-center gap-1.5">
                          <Zap className="h-3 w-3" style={{ color: LIGHT[f.light] }} />
                          {f.sessions?.length ? `${f.sessions.length} session${f.sessions.length > 1 ? "s" : ""} to fuel` : "Rest day"}
                        </span>
                      )}
                    </p>
                    {!isRace && (f?.sessions || []).map((s, si) => (
                      <p key={si} className="text-[11px] text-zinc-300 mt-1.5 border-t border-white/8 pt-1.5">
                        {s.sport} · {s.duration} min — {s.preG}g before, {s.duringPerHr}g/hr during, {s.postG}g after
                      </p>
                    ))}
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}

        <ChecklistBlock raceIso={raceIso} />

        <p className="text-[10px] text-zinc-500">
          SPC Performance Nutrition · {name} · race day {raceIso} · {fmtMin(n.durMin)} ·
          guidance only, not medical advice.
        </p>
      </div>
    </div>
  );
}
